import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { FieldError } from 'react-hook-form';
import { Palette, Package, MessageSquare } from 'lucide-react';
import { FormInput } from './FormInput';
import { FormTextarea } from './FormTextarea';
import { FormSelect } from './FormSelect';
import { FormCheckboxGroup } from './FormCheckboxGroup';
import { FileUpload } from './FileUpload';
import { Button } from '../ui/Button';
import { SubmissionProgress } from '../ui/SubmissionProgress';
import { useFormSubmit } from '../../hooks/useFormSubmit';
import { useAuthUser } from '../../hooks/useAuthUser';
import {
  marketingFormSchema,
  type MarketingFormValues,
  type RequestType,
  requestTypes,
  collateralNeedOptions,
  departmentOptions,
} from '../../lib/validation';
import { getMinDate } from '../../lib/utils';

const needOptions = collateralNeedOptions.map((opt) => ({
  value: opt,
  label: opt,
}));

const deptOptions = departmentOptions.map((opt) => ({
  value: opt,
  label: opt,
}));

export function MarketingForm() {
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const [files, setFiles] = useState<File[]>([]);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submissionError, setSubmissionError] = useState<string>('');
  const { submit, isSubmitting, error } = useFormSubmit<MarketingFormValues>();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MarketingFormValues>({
    resolver: zodResolver(marketingFormSchema),
    defaultValues: {
      requestType: 'collateral',
      name: authUser.name || '',
      email: authUser.email || '',
      department: '',
      needByDate: '',
      whatDoYouNeed: [],
      projectName: '',
      goal: '',
      audienceAndTopic: '',
      description: '',
      wordCount: '',
      imageSizeDimensions: '',
      fileTypeNeeded: '',
      otherDescription: '',
      videoOrDropboxLink: '',
    } as MarketingFormValues,
  });

  // Auto-fill name and email from SSO when available
  useEffect(() => {
    if (authUser.isAuthenticated) {
      if (authUser.name) setValue('name', authUser.name);
      if (authUser.email) setValue('email', authUser.email);
    }
  }, [authUser.isAuthenticated, authUser.name, authUser.email, setValue]);

  const requestType = watch('requestType') as RequestType;
  const whatDoYouNeed = requestType === 'collateral' ? (watch('whatDoYouNeed' as never) ?? []) : [];
  const showOtherDescription =
    requestType === 'collateral' && Array.isArray(whatDoYouNeed) && whatDoYouNeed.includes('Other');
  const showVideoLink =
    requestType === 'collateral' && Array.isArray(whatDoYouNeed) && whatDoYouNeed.includes('Edit to existing video');

  const handleTypeChange = (newType: RequestType) => {
    // Reset form but keep shared fields
    const currentName = watch('name');
    const currentEmail = watch('email');
    const currentDept = watch('department' as never) as unknown as string;
    const currentDate = watch('needByDate');

    if (newType === 'collateral') {
      reset({
        requestType: 'collateral',
        name: currentName,
        email: currentEmail,
        department: currentDept,
        needByDate: currentDate,
        whatDoYouNeed: [],
        projectName: '',
        goal: '',
        audienceAndTopic: '',
        description: '',
        wordCount: '',
        imageSizeDimensions: '',
        fileTypeNeeded: '',
        otherDescription: '',
        videoOrDropboxLink: '',
      } as MarketingFormValues);
    } else if (newType === 'trade-show') {
      reset({
        requestType: 'trade-show',
        name: currentName,
        email: currentEmail,
        department: currentDept,
        needByDate: currentDate,
        titleOfRequest: '',
        whoIsRequestFor: '',
        whatDoYouNeedText: '',
        address: '',
        tradeShowDemoDetails: '',
        reasonForRequest: '',
      } as MarketingFormValues);
    } else {
      reset({
        requestType: 'quick-note',
        name: currentName,
        email: currentEmail,
        department: currentDept,
        needByDate: currentDate,
        subject: '',
        message: '',
      } as MarketingFormValues);
    }
    setFiles([]);
  };

  const onSubmit = async (data: MarketingFormValues) => {
    setSubmissionStatus('submitting');
    setSubmissionError('');

    const result = await submit(data, data.requestType, files);

    if (result.success) {
      setSubmissionStatus('success');
      setTimeout(() => {
        const title =
          data.requestType === 'collateral'
            ? (data as { projectName: string }).projectName
            : data.requestType === 'trade-show'
              ? (data as { titleOfRequest: string }).titleOfRequest
              : (data as { subject: string }).subject || 'Quick Note';

        navigate('/success', {
          state: {
            formType:
              data.requestType === 'collateral'
                ? 'Marketing Collateral Request'
                : data.requestType === 'trade-show'
                  ? 'Marketing Request'
                  : 'Quick Note to Marketing',
            reviewTeam: 'marketing team',
            ticketId: result.ticketId,
            title,
          },
        });
      }, 2000);
    } else {
      setSubmissionStatus('error');
      setSubmissionError(result.message || 'Submission failed. Please try again.');
    }
  };

  return (
    <>
      <SubmissionProgress
        status={submissionStatus}
        errorMessage={submissionError}
        onDismissError={() => setSubmissionStatus('idle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* ── Request Type Selector ─────────────────────────────── */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-900">
            What type of request is this? <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {requestTypes.map((type) => {
              const isActive = requestType === type.value;
              const Icon = type.value === 'collateral' ? Palette : type.value === 'trade-show' ? Package : MessageSquare;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeChange(type.value)}
                  className={`
                    flex flex-col items-center gap-3 p-5 rounded-xl border-2 text-center
                    transition-all duration-200
                    ${
                      isActive
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border bg-white hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                      ${isActive ? 'bg-primary/10' : 'bg-gray-100'}
                    `}
                  >
                    <Icon
                      className={`w-5 h-5 ${isActive ? 'text-primary' : 'text-gray-500'}`}
                    />
                  </div>
                  <div>
                    <p
                      className={`font-medium ${
                        isActive ? 'text-primary' : 'text-gray-900'
                      }`}
                    >
                      {type.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <input type="hidden" {...register('requestType')} />
        </div>

        {/* ── Shared Fields ─────────────────────────────────────── */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Your Information
            </h3>
            {authUser.isAuthenticated && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-sm">
                ✓ Signed in via OneLogin
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Name"
              placeholder="Your full name"
              {...register('name')}
              error={errors.name}
              required
              readOnly={authUser.isAuthenticated && !!authUser.name}
              className={authUser.isAuthenticated && authUser.name ? 'bg-gray-50' : ''}
            />
            <FormInput
              label="Email"
              type="email"
              placeholder="you@starmicronics.com"
              {...register('email')}
              error={errors.email}
              required
              readOnly={authUser.isAuthenticated && !!authUser.email}
              className={authUser.isAuthenticated && authUser.email ? 'bg-gray-50' : ''}
            />
          </div>
          <Controller
            name={'department' as never}
            control={control}
            render={({ field }) => (
              <FormSelect
                label="Department"
                options={deptOptions}
                placeholder="Select your department"
                value={(field.value as string) ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                error={(errors as Record<string, FieldError>).department}
                required
              />
            )}
          />
          <FormInput
            label="When do you need this by?"
            type="date"
            min={getMinDate()}
            {...register('needByDate')}
            error={errors.needByDate}
            helperText="Please give us at least 2 weeks if possible"
            required
          />
        </div>

        {/* ── Collateral-Specific Fields ────────────────────────── */}
        {requestType === 'collateral' && (
          <div className="space-y-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-border pb-2">
              Collateral Details
            </h3>

            <p className="text-sm text-gray-500">
              Be as specific as possible. If you need to discuss the request, schedule a meeting
              with us after submitting.
            </p>

            <Controller
              name={'whatDoYouNeed' as never}
              control={control}
              render={({ field }) => (
                <FormCheckboxGroup
                  name="whatDoYouNeed"
                  label="What do you need?"
                  options={needOptions}
                  value={(field.value as string[]) ?? []}
                  onChange={field.onChange}
                  error={(errors as Record<string, FieldError>).whatDoYouNeed}
                  required
                />
              )}
            />

            {showOtherDescription && (
              <FormTextarea
                label="Please describe what you need"
                placeholder="Tell us more about your request..."
                {...register('otherDescription' as never)}
                error={(errors as Record<string, FieldError>).otherDescription}
              />
            )}

            {showVideoLink && (
              <FormInput
                label="Dropbox / Video Link"
                placeholder="https://www.dropbox.com/..."
                {...register('videoOrDropboxLink' as never)}
                error={(errors as Record<string, FieldError>).videoOrDropboxLink}
                helperText="Paste the link to the existing video you'd like edited"
                required
              />
            )}

            <FormInput
              label="Project Name"
              placeholder="e.g. Q1 Product Launch Brochure"
              {...register('projectName' as never)}
              error={(errors as Record<string, FieldError>).projectName}
              required
            />

            <FormTextarea
              label="Goal"
              placeholder="What is the goal of this request? e.g. Increase awareness of new product line"
              rows={3}
              {...register('goal' as never)}
              error={(errors as Record<string, FieldError>).goal}
              required
            />

            <FormTextarea
              label="Audience & Topic"
              placeholder="Who is this for and what's the topic? e.g. Sales team, product training"
              rows={3}
              {...register('audienceAndTopic' as never)}
              error={(errors as Record<string, FieldError>).audienceAndTopic}
              required
            />

            <FormTextarea
              label="Description"
              placeholder="Describe what you need in detail..."
              rows={4}
              {...register('description' as never)}
              error={(errors as Record<string, FieldError>).description}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormInput
                label="Word Count"
                placeholder="e.g. 500 words"
                {...register('wordCount' as never)}
                error={(errors as Record<string, FieldError>).wordCount}
                helperText="If applicable"
              />
              <FormInput
                label="Image Size / Dimensions"
                placeholder="e.g. 1920x1080px, 300 DPI"
                {...register('imageSizeDimensions' as never)}
                error={(errors as Record<string, FieldError>).imageSizeDimensions}
                helperText="If applicable"
              />
              <FormInput
                label="File Type Needed"
                placeholder="e.g. PDF, PNG, DOCX"
                {...register('fileTypeNeeded' as never)}
                error={(errors as Record<string, FieldError>).fileTypeNeeded}
                required
              />
            </div>

            <FileUpload
              name="attachments"
              label="Attachments"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.ai,.psd,.eps,.svg"
              maxFiles={5}
              maxSizeMB={10}
              onChange={setFiles}
            />
          </div>
        )}

        {/* ── Trade Show / Orders-Specific Fields ───────────────── */}
        {requestType === 'trade-show' && (
          <div className="space-y-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-border pb-2">
              Request Details
            </h3>

            <p className="text-sm text-gray-500">
              Please be as detailed as possible. Include part numbers and quantities when available.
            </p>

            <FormInput
              label="Title of Request"
              placeholder="e.g. CES 2025 Booth Setup"
              {...register('titleOfRequest' as never)}
              error={(errors as Record<string, FieldError>).titleOfRequest}
              required
            />

            <FormTextarea
              label="Who is this for?"
              placeholder="e.g. Vendor, myself, sales team"
              rows={2}
              {...register('whoIsRequestFor' as never)}
              error={(errors as Record<string, FieldError>).whoIsRequestFor}
              required
            />

            <FormTextarea
              label="What do you need?"
              placeholder="e.g. TSP143IVUE Qty: 1, Color: BLK, Working: Yes&#10;Retail Brochures Qty: 20"
              rows={5}
              {...register('whatDoYouNeedText' as never)}
              error={(errors as Record<string, FieldError>).whatDoYouNeedText}
              helperText="Include product names, part numbers, quantities, and whether items need to be functional"
              required
            />

            <FormTextarea
              label="Shipping Address"
              placeholder="Trade show location, office address, etc."
              rows={3}
              {...register('address' as never)}
              error={(errors as Record<string, FieldError>).address}
            />

            <FormTextarea
              label="Demo / Setup Details"
              placeholder="e.g. Will connect to my phone, just for display, need iOS tablet"
              rows={3}
              {...register('tradeShowDemoDetails' as never)}
              error={(errors as Record<string, FieldError>).tradeShowDemoDetails}
              helperText="If this is a trade show or demo, what's needed to run the setup?"
            />

            <FormTextarea
              label="Reason for Request"
              placeholder="Optional — why is this needed?"
              rows={2}
              {...register('reasonForRequest' as never)}
              error={(errors as Record<string, FieldError>).reasonForRequest}
            />

            <FileUpload
              name="attachments"
              label="Attachments"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              maxFiles={5}
              maxSizeMB={10}
              onChange={setFiles}
            />
          </div>
        )}

        {/* ── Quick Note Fields ───────────────────────────────────── */}
        {requestType === 'quick-note' && (
          <div className="space-y-6 animate-fade-in">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-border pb-2">
              Your Message
            </h3>

            <p className="text-sm text-gray-500">
              Drop a quick note to the marketing team. No need to fill out a full form — just tell us what you need.
            </p>

            <FormInput
              label="Subject"
              placeholder="e.g. Homepage banner update, Quick graphic for presentation"
              {...register('subject' as never)}
              error={(errors as Record<string, FieldError>).subject}
              required
            />

            <FormTextarea
              label="Message"
              placeholder="What would you like marketing to know? e.g. Can we update the homepage banner? I need a quick graphic for a presentation tomorrow."
              rows={6}
              {...register('message' as never)}
              error={(errors as Record<string, FieldError>).message}
              required
            />
          </div>
        )}

        {/* ── Actions ───────────────────────────────────────────── */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={() => navigate('/portal')}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Submit Request
          </Button>
        </div>
      </form>
    </>
  );
}
